import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { cn } from '../../lib/utils';
import { VacationRequest, VacationRequestStatus } from '../../types';
import { VacationRequestCard } from './VacationRequestCard';
import { VacationPagination } from './VacationPagination';

type RequestTab =
  | 'ALL'
  | VacationRequestStatus.PENDING
  | VacationRequestStatus.APPROVED
  | VacationRequestStatus.REJECTED
  | VacationRequestStatus.CANCELLED;

const TABS: RequestTab[] = [
  'ALL',
  VacationRequestStatus.PENDING,
  VacationRequestStatus.APPROVED,
  VacationRequestStatus.REJECTED,
  VacationRequestStatus.CANCELLED,
];

const tabLabelKey: Record<RequestTab, string> = {
  ALL: 'myRequests.tabs.all',
  [VacationRequestStatus.PENDING]: 'myRequests.tabs.pending',
  [VacationRequestStatus.APPROVED]: 'myRequests.tabs.approved',
  [VacationRequestStatus.REJECTED]: 'myRequests.tabs.rejected',
  [VacationRequestStatus.CANCELLED]: 'myRequests.tabs.cancelled',
};

const ITEMS_PER_PAGE = 5;

interface MyVacationRequestsProps {
  requests: VacationRequest[];
  loading?: boolean;
  isSubmitting?: boolean;
  onCancel?: (request: VacationRequest) => void;
}

/**
 * Member-facing "My requests" panel: a titled card with All / Pending /
 * Approved tabs and a divided list of {@link VacationRequestCard} rows. Tab
 * filtering is handled client-side over the requests passed in.
 */
export const MyVacationRequests: React.FC<MyVacationRequestsProps> = ({
  requests,
  loading,
  isSubmitting,
  onCancel,
}) => {
  const { t } = useTranslation('vacations');
  const [activeTab, setActiveTab] = useState<RequestTab>('ALL');
  const [page, setPage] = useState(1);

  const visibleRequests = useMemo(() => {
    if (activeTab === 'ALL') return requests;
    return requests.filter((req) => req.status === activeTab);
  }, [requests, activeTab]);

  const totalPages = Math.max(1, Math.ceil(visibleRequests.length / ITEMS_PER_PAGE));

  // Reset to the first page whenever the tab or the underlying list changes,
  // and clamp if the current page falls out of range.
  useEffect(() => setPage(1), [activeTab]);
  useEffect(() => {
    setPage((prev) => Math.min(prev, totalPages));
  }, [totalPages]);

  const pagedRequests = useMemo(
    () => visibleRequests.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE),
    [visibleRequests, page],
  );

  return (
    <Card data-testid="vacations-card-list">
      <CardHeader>
        <CardTitle className="text-lg">{t('myRequests.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="mb-2 flex gap-6 border-b border-border" role="tablist">
          {TABS.map((tab) => (
            <button
              key={tab}
              type="button"
              role="tab"
              aria-selected={activeTab === tab}
              onClick={() => setActiveTab(tab)}
              className={cn(
                '-mb-px border-b-2 pb-2 text-sm font-medium transition-colors',
                activeTab === tab
                  ? 'border-orange-500 text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground',
              )}
              data-testid={`vacations-tab-${tab.toLowerCase()}`}
            >
              {t(tabLabelKey[tab])}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="h-24 animate-pulse rounded-lg bg-muted" />
        ) : visibleRequests.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">
            {t('table.empty')}
          </div>
        ) : (
          <>
            <div className="divide-y divide-border">
              {pagedRequests.map((request) => (
                <VacationRequestCard
                  key={request.id}
                  request={request}
                  isSubmitting={isSubmitting}
                  onCancel={onCancel}
                />
              ))}
            </div>
            <VacationPagination
              currentPage={page}
              totalPages={totalPages}
              totalItems={visibleRequests.length}
              itemsPerPage={ITEMS_PER_PAGE}
              onPageChange={setPage}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
};
